/**
 * Permission catalogue. The backend enforces these on every endpoint, query,
 * realtime subscription and media request; the UI only uses them to hide
 * controls.
 */
export const PERMISSIONS = [
  'system.manage',
  'users.manage',
  'connections.manage',
  'settings.manage',
  'crm.read',
  'crm.write',
  'contacts.merge',
  'contacts.phone_full',
  'data.export',
  'registrations.write',
  'payments.read',
  'payments.record',
  'payments.verify',
  'folders.manage',
  'segments.manage',
  'conversations.read_all',
  'conversations.read_assigned',
  'conversations.reply',
  'conversations.assign',
  'campaigns.read',
  'campaigns.manage',
  'campaigns.bulk_send',
  'deliveries.retry',
  'notifications.manage',
  'ai.manage',
  'knowledge.manage',
  'reports.read',
  'audit.read',
  'imports.manage',
  'sync.manage',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

export const ROLES = ['owner', 'admin', 'agent', 'finance', 'viewer'] as const;
export type Role = (typeof ROLES)[number];

const ALL: readonly Permission[] = PERMISSIONS;

export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  owner: ALL,
  admin: ALL.filter((p) => !['system.manage', 'users.manage', 'connections.manage'].includes(p)),
  agent: [
    'crm.read',
    'crm.write',
    'registrations.write',
    'payments.read',
    'payments.record',
    'conversations.read_assigned',
    'conversations.reply',
    'campaigns.read',
    'reports.read',
  ],
  finance: [
    'crm.read',
    'payments.read',
    'payments.record',
    'payments.verify',
    'conversations.read_assigned',
    'reports.read',
    'contacts.phone_full',
  ],
  viewer: ['crm.read', 'payments.read', 'conversations.read_assigned', 'campaigns.read', 'reports.read'],
};

export function effectivePermissions(role: Role, granted: readonly string[] = [], revoked: readonly string[] = []): Set<Permission> {
  const set = new Set<Permission>(ROLE_PERMISSIONS[role]);
  for (const g of granted) if ((PERMISSIONS as readonly string[]).includes(g)) set.add(g as Permission);
  for (const r of revoked) set.delete(r as Permission);
  // Owners can never lose the ability to manage users (avoid lock-out).
  if (role === 'owner') {
    set.add('users.manage');
    set.add('system.manage');
  }
  return set;
}

export const PERMISSION_LABELS_TH: Record<Permission, string> = {
  'system.manage': 'จัดการระบบ',
  'users.manage': 'จัดการผู้ใช้และสิทธิ์',
  'connections.manage': 'จัดการบัญชีเชื่อมต่อ',
  'settings.manage': 'ตั้งค่าระบบ',
  'crm.read': 'ดูข้อมูล CRM',
  'crm.write': 'แก้ไขข้อมูล CRM',
  'contacts.merge': 'รวมผู้ติดต่อ',
  'contacts.phone_full': 'ดูเบอร์โทรเต็ม',
  'data.export': 'ส่งออกข้อมูล',
  'registrations.write': 'แก้ไขใบสมัคร',
  'payments.read': 'ดูสถานะการเงิน',
  'payments.record': 'บันทึกรายการเงิน (รอยืนยัน)',
  'payments.verify': 'ยืนยัน/ปฏิเสธรายการเงิน',
  'folders.manage': 'จัดการหมวดหมู่',
  'segments.manage': 'จัดการกลุ่มตามเงื่อนไข',
  'conversations.read_all': 'ดูแชททั้งหมด',
  'conversations.read_assigned': 'ดูแชทที่ได้รับมอบหมาย',
  'conversations.reply': 'ตอบแชท',
  'conversations.assign': 'มอบหมายแชท',
  'campaigns.read': 'ดูการส่งข้อความ',
  'campaigns.manage': 'สร้าง/แก้ไขการส่งข้อความ',
  'campaigns.bulk_send': 'ยืนยันส่งข้อความจำนวนมาก',
  'deliveries.retry': 'ลองส่งใหม่',
  'notifications.manage': 'จัดการการแจ้งเตือน',
  'ai.manage': 'ตั้งค่า AI',
  'knowledge.manage': 'จัดการฐานความรู้',
  'reports.read': 'ดูรายงาน',
  'audit.read': 'ดูประวัติการแก้ไข',
  'imports.manage': 'นำเข้าข้อมูล',
  'sync.manage': 'ซิงก์ข้อมูลระบบสมัคร',
};
